import { NavLink, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { PRIMARY_NAV, SECONDARY_NAV } from "@/components/nav-config";
import { useSubscription } from "@/context/SubscriptionContext";

function NavItem({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
          isActive
            ? "bg-accent/10 text-accent font-semibold"
            : "text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-accent" : ""}`} />
          <span className="flex-1 truncate">{item.label}</span>
          {!item.ready && (
            <span className="rounded-full bg-[hsl(var(--sidebar-hover-bg))] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-[hsl(var(--sidebar-text))]">
              Soon
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function SidebarContent({ onNavigate }) {
  const { subscription } = useSubscription();


  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-[hsl(var(--sidebar-border))] px-5">
        <Link to="/" className="flex items-center transition-opacity hover:opacity-90">
          <Logo />
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--sidebar-text))]/60">
          Workspace
        </p>
        <div id="tour-sidebar-workspace" className="space-y-0.5">
          {PRIMARY_NAV.map((item) => (
            <NavItem key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </div>

        <div className="mt-6">
          <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--sidebar-text))]/60">
            Account
          </p>
          <div className="space-y-0.5">
            {SECONDARY_NAV.map((item) => (
              <NavItem key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </nav>

      {/* Subscription Status Card in Sidebar */}
      <div className="p-3">
        <Link
          to="/pricing"
          onClick={onNavigate}
          className="flex items-center justify-between rounded-xl border border-border bg-surface px-3 py-2.5 text-xs font-semibold text-foreground transition-all hover:bg-elevated"
        >
          <div className="truncate">
            <span className="block truncate font-bold text-foreground">
              {subscription?.active
                ? subscription.plan_name
                : "Free Access"}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {subscription?.is_admin
                ? "Admin Unlimited"
                : subscription?.days_remaining
                ? `${subscription.days_remaining}d remaining`
                : "View plans & billing"}
            </span>
          </div>
          <span className="text-[10px] font-bold text-accent">
            {subscription?.active ? "Active" : "Plans →"}
          </span>
        </Link>
      </div>

      {/* Bottom brand strip & legal links */}
      <div className="border-t border-[hsl(var(--sidebar-border))] px-4 py-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            <p className="text-[10px] font-medium text-[hsl(var(--sidebar-text))]/60">Virality AI</p>
          </div>
          <span className="text-[9px] text-[hsl(var(--sidebar-text))]/40">v1.0</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[hsl(var(--sidebar-text))]/50">
          <Link to="/privacy" onClick={onNavigate} className="hover:text-accent transition-colors" data-testid="sidebar-privacy">Privacy</Link>
          <span>·</span>
          <Link to="/terms" onClick={onNavigate} className="hover:text-accent transition-colors" data-testid="sidebar-terms">Terms</Link>
          <span>·</span>
          <Link to="/cookies" onClick={onNavigate} className="hover:text-accent transition-colors" data-testid="sidebar-cookies">Cookies</Link>
        </div>
      </div>
    </div>
  );
}

